
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Edit3, Save, X } from "lucide-react";
import { LevelNetworkRulesTable } from "./LevelNetworkRulesTable";
import { LevelNetworkRulesEditMode } from "./LevelNetworkRulesEditMode";

interface LevelFeaturesSectionProps {
  levelId: string;
}

export const LevelFeaturesSection = ({ levelId }: LevelFeaturesSectionProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("system-wide");

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
  };

  const handleSave = () => {
    // Save functionality will be handled by the edit mode component
    setIsEditMode(false);
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Features</CardTitle>
          <div className="flex items-center gap-2">
            {isEditMode ? (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCancel}
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSave}
                  className="flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </Button>
              </>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleEditToggle}
                className="flex items-center gap-2"
              >
                <Edit3 className="w-4 h-4" />
                Edit Features
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="system-wide">System-Wide Features</TabsTrigger>
              <TabsTrigger value="app-specific">App-Specific Features</TabsTrigger>
            </TabsList>
            
            <TabsContent value="system-wide" className="mt-6">
              {isEditMode ? (
                <LevelNetworkRulesEditMode 
                  levelId={levelId}
                  ruleType="system-wide"
                />
              ) : (
                <LevelNetworkRulesTable 
                  levelId={levelId}
                  ruleType="system-wide"
                />
              )}
            </TabsContent>
            
            <TabsContent value="app-specific" className="mt-6">
              {isEditMode ? (
                <LevelNetworkRulesEditMode 
                  levelId={levelId}
                  ruleType="app-specific"
                />
              ) : (
                <LevelNetworkRulesTable 
                  levelId={levelId}
                  ruleType="app-specific"
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
